/**
 * @fileoverview OTP verification page
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { LandingNavbar } from '@/components/layout/LandingNavbar';
import { useLanguage } from '@/contexts/LanguageContext';
import { apiService } from '@/services/api/apiService';
import { otpService } from '@/services/auth/otpService';

export const OTPPage = () => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const navigate = useNavigate();
  const { login, user, updateUser } = useAuth();
  const { t } = useLanguage();

  useEffect(() => {
    // Check if we are in a 2FA login flow or a setup flow
    const pendingEmail = sessionStorage.getItem('pendingLoginEmail');
    const isSetup = sessionStorage.getItem('is2FASetup') === 'true';

    if (!pendingEmail && !isSetup) {
      navigate('/login');
      return;
    }

    setEmail(pendingEmail || user?.email || '');
  }, [navigate]);

  const handleOTPChange = (index, value) => {
    if (value.length > 1) {
      // Paste handling
      const pastedOTP = value.slice(0, 6).split('');
      const newOTP = [...otp];
      pastedOTP.forEach((char, i) => {
        if (index + i < 6 && /^\d$/.test(char)) {
          newOTP[index + i] = char;
        }
      });
      setOtp(newOTP);

      // Focus next empty input or submit
      const nextIndex = Math.min(index + pastedOTP.length, 5);
      const nextInput = document.getElementById(`otp-${nextIndex}`);
      if (nextInput) {
        nextInput.focus();
      } else if (newOTP.every(digit => digit !== '')) {
        handleSubmit();
      }
      return;
    }

    // Single digit input
    if (!/^\d$/.test(value) && value !== '') {
      return;
    }

    const newOTP = [...otp];
    newOTP[index] = value;
    setOtp(newOTP);
    setError('');

    // Auto-focus next input
    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      if (nextInput) {
        nextInput.focus();
      }
    }

  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      if (prevInput) {
        prevInput.focus();
      }
    }
  };

  const handleSubmit = async (currentOtp = otp) => {
    const otpString = currentOtp.join('');

    if (otpString.length !== 6) {
      setError(t('auth.complete_code', 'Por favor, ingrese el código completo de 6 dígitos'));
      return;
    }

    setIsVerifying(true);
    setError('');

    try {
      const isSetup = sessionStorage.getItem('is2FASetup') === 'true';

      if (isSetup) {
        // Verification for SETUP flow in Settings
        await apiService.request('/two-factor/verify-and-enable', {
          method: 'POST',
          body: JSON.stringify({ token: otpString })
        });
        updateUser({ twoFactorEnabled: true });
        sessionStorage.removeItem('is2FASetup');
        navigate('/settings?2fa=enabled');
      } else {
        // Verification for LOGIN flow
        const data = await apiService.request('/auth/verify-2fa', {
          method: 'POST',
          body: JSON.stringify({ email, token: otpString }),
        });

        login(data.user, data.token);

        // Clear session storage
        sessionStorage.removeItem('pendingLoginEmail');
        sessionStorage.removeItem('pendingLoginRole');

        // Redirect based on role
        const role = data.user.role || 'certifier';
        if (role === 'certifier') {
          navigate('/courses');
        } else {
          navigate('/my-documents');
        }
      }
    } catch (err) {
      setError(err.message || t('common.connection_error', 'Error de conexión con el servidor'));
      console.error(err);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendOTP = async () => {
    if (resendCooldown > 0) return;

    setError('');
    setOtp(['', '', '', '', '', '']);
    setResendCooldown(60);

    try {
      await otpService.sendOTP(email);

      // Start cooldown timer
      const cooldownInterval = setInterval(() => {
        setResendCooldown((prev) => {
          if (prev <= 1) {
            clearInterval(cooldownInterval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (err) {
      setError(err.message || t('auth.resend_error', 'Error al reenviar el código'));
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <LandingNavbar />

      <div className="flex-1 flex flex-col lg:flex-row">
        {/* Columna Izquierda - Información (Hidden on mobile) */}
        <div className="hidden lg:flex lg:w-1/2 bg-[#192A42] text-white p-12 xl:p-24 flex-col justify-center relative overflow-hidden">
          {/* Background Patterns */}
          <div className="absolute inset-0 z-0">
            <div className="absolute inset-0 bg-[url('/patterns/cubes.png')] opacity-[0.05]"></div>
            <div className="absolute top-0 right-0 -mr-20 -mt-20 w-[600px] h-[600px] bg-blue-500/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-[500px] h-[500px] bg-emerald-500/10 rounded-full blur-3xl"></div>
          </div>

          <div className="relative z-10 space-y-8">
            <h2 className="text-4xl xl:text-5xl font-extrabold leading-tight text-white">
              {t('auth.2fa_verify_title', 'Verificación')} <br />
              <span className="text-white">{t('auth.2fa_verify_subtitle', 'Segura y Rápida')}</span>
            </h2>
            <p className="text-slate-300 text-lg leading-relaxed max-w-lg">
              {t('auth.2fa_verify_desc', 'Protegemos tu cuenta con autenticación de dos factores. Tu seguridad es nuestra prioridad.')}
            </p>

            <ul className="space-y-6 pt-4">
              {[
                { text: t('auth.otp_one_time', 'Código de uso único'), icon: 'key' },
                { text: t('auth.otp_expires', 'Expira en 5 minutos'), icon: 'clock' },
                { text: t('auth.otp_secure', 'Acceso seguro garantizado'), icon: 'lock' }
              ].map((item, idx) => (
                <li key={idx} className="flex items-center gap-4 bg-white/5 p-4 rounded-xl backdrop-blur-sm border border-white/10">
                  <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-400 shrink-0">
                    <i className={`fas fa-${item.icon}`}></i>
                  </div>
                  <span className="font-medium">{item.text}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Columna Derecha - Formulario */}
        <div className="flex-1 flex flex-col justify-center items-center p-4 sm:p-8 lg:p-12 lg:w-1/2 bg-white">
          <div className="w-full max-w-md space-y-8">


            <div className="text-center lg:text-left space-y-2">
              <h1 className="text-3xl font-extrabold text-slate-900">{t('auth.enter_code', 'Ingresa el Código')}</h1>
              <p className="text-slate-500">
                {sessionStorage.getItem('is2FASetup') === 'true'
                  ? t('auth.2fa_setup_desc', 'Ingresa el código que aparece en tu aplicación Google Authenticator')
                  : `${t('auth.code_sent', 'Hemos enviado un código a')} ${email}`}
              </p>
            </div>

            <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }} className="space-y-8 mt-8">
              <div className="space-y-4">
                <label className="text-sm font-semibold text-slate-700 ml-1">
                  {t('auth.6_digits', 'Código de 6 dígitos')}
                </label>
                <div className="flex gap-2 sm:gap-4 justify-between">
                  {otp.map((digit, index) => (
                    <input
                      key={index}
                      id={`otp-${index}`}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      className="w-12 h-14 sm:w-14 sm:h-16 text-center text-2xl sm:text-3xl font-bold rounded-xl border-2 border-slate-200 bg-slate-50 focus:bg-white focus:border-blue-500 focus:ring-0 transition-all outline-none caret-blue-500"
                      value={digit}
                      onChange={(e) => handleOTPChange(index, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(index, e)}
                      autoFocus={index === 0}
                    />
                  ))}
                </div>
                <p className="text-xs text-slate-500 flex items-center gap-1.5 ml-1 justify-center lg:justify-start">
                  <i className="fas fa-info-circle text-blue-500"></i>
                  {t('auth.check_inbox', 'Revisa tu bandeja de entrada o spam')}
                </p>
              </div>

              {error && (
                <div className="p-4 rounded-xl bg-red-50 border border-red-100 text-red-600 flex items-start gap-3 animate-fade-in-up">
                  <i className="fas fa-exclamation-circle mt-0.5 shrink-0"></i>
                  <p className="text-sm font-medium">{error}</p>
                </div>
              )}

              <button
                type="submit"
                className="w-full py-4 rounded-xl bg-blue-600 hover:bg-blue-700 active:scale-[0.98] text-white font-bold text-lg shadow-lg hover:shadow-blue-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-3"
                disabled={isVerifying || otp.some(digit => !digit)}
              >
                {isVerifying ? (
                  <>
                    <i className="fas fa-circle-notch fa-spin"></i>
                    {t('common.verifying', 'Verificando...')}
                  </>
                ) : (
                  <>
                    <i className="fas fa-check-circle"></i>
                    <span>{t('auth.verify_code', 'Verificar Código')}</span>
                  </>
                )}
              </button>

              <div className="flex flex-col gap-4 text-center">
                {sessionStorage.getItem('is2FASetup') !== 'true' && (
                  <div className="text-sm text-slate-600">
                    {t('auth.no_code', '¿No recibiste el código?')} {' '}
                    <button
                      type="button"
                      className="font-bold text-blue-600 hover:text-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      onClick={handleResendOTP}
                      disabled={resendCooldown > 0}
                    >
                      {resendCooldown > 0 ? `${t('auth.resend_in', 'Reenviar en')} ${resendCooldown}s` : t('auth.resend_now', 'Reenviar ahora')}
                    </button>
                  </div>
                )}

                <button
                  type="button"
                  className="text-sm text-slate-400 hover:text-slate-600 transition-colors flex items-center justify-center gap-2"
                  onClick={() => {
                    sessionStorage.removeItem('pendingLoginEmail');
                    sessionStorage.removeItem('pendingLoginRole');
                    navigate('/login');
                  }}
                >
                  <i className="fas fa-arrow-left text-xs"></i>
                  {t('auth.back_email', 'Volver a ingresar email')}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

